import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useRequest, useUpdateRequest } from "../../api/developmentRequests";
import BackButton from "../../components/ui/BackButton";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { FilterBar } from "../../components/ui/FilterBar";
import { Form } from "../../components/ui/Form";
import { Field } from "../../components/ui/Field";
import { Page } from "../../components/ui/Page";
import { Switch } from "../../components/ui/Switch";
import PageLoader from "../../components/ui/PageLoader";
import { Section } from "../../components/ui/Section";

import { DepartmentSelect } from "../../components/ui/DepartmentSelect";
import { UserSelect } from "../../components/ui/UserSelect";
import { UserSingleSelect } from "../../components/ui/UserSingleSelect";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useLayoutTitle } from "../../hooks/useLayoutTitle";
import { useTranslation } from "../../hooks/useTranslation";
import type { DevelopmentRequestResponse, UpdateDevelopmentRequestRequest } from "../../types/developmentRequest";
import type { UserLightResponse } from "../../types/user";
import { hasTrimmedValue, isOptionalNonNegativeInteger, toOptionalNumber } from "../../utils/formValidation";
import styles from "./EditRequestPage.module.css";

interface EditFormValues {
  title: string;
  description: string;
  audience: "internal" | "external";
  departments: string[];
  needsErpIntegration: boolean;
  participants: UserLightResponse[];
  responsibleDepartment: DevelopmentRequestResponse["responsibleDepartment"] | "";
  estimateDays?: number;
  internalNotes: string;
  status: DevelopmentRequestResponse["status"];
  supervisor: UserLightResponse | null;
}

function toFormValues(request: DevelopmentRequestResponse): EditFormValues {
  return {
    title: request.title,
    description: request.description,
    audience: request.audience,
    departments: request.departments,
    needsErpIntegration: request.needsErpIntegration,
    participants: request.participants,
    responsibleDepartment: request.responsibleDepartment ?? "",
    estimateDays: request.estimateDays ?? undefined,
    internalNotes: request.internalNotes ?? "",
    status: request.status,
    supervisor: request.supervisor ?? null,
  };
}

export default function EditRequestPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: request, isLoading } = useRequest(Number(id) ?? 0);
  useLayoutTitle(request ? t("requests.editRequestPageTitle", { title: request.title }) : t("requests.allRequests"));
  const currentUser = useCurrentUser();
  const updateRequest = useUpdateRequest(Number(id) ?? 0);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EditFormValues>({
    values: request ? toFormValues(request) : undefined,
  });

  if (isLoading) return <PageLoader />;
  if (!request) return <p className={styles.emptyText}>{t("common.noData")}</p>;

  const isAdmin = currentUser?.isAdmin === true;
  const isAuthor = currentUser?.id === request.createdBy.id;

  if (!isAdmin && !isAuthor) {
    navigate(`/requests/${id}`, { replace: true });
    return null;
  }

  const locked = request.status !== "new" && !isAdmin;

  const responsibleDepartmentOptions = [
    { value: "", label: "—" },
    { value: "integrations", label: t("requests.responsibleDepartmentIntegrations") },
    { value: "software_development", label: t("requests.responsibleDepartmentSoftwareDevelopment") },
    { value: "ict", label: t("requests.responsibleDepartmentIct") },
  ];

  const statusOptions = [
    { value: "new", label: t("requests.statusNew") },
    { value: "in_progress", label: t("requests.statusInProgress") },
    { value: "completed", label: t("requests.statusCompleted") },
  ];

  const onSubmit = handleSubmit((values) => {
    const payload: UpdateDevelopmentRequestRequest = {
      title: values.title.trim(),
      description: values.description.trim(),
      audience: values.audience,
      departments: values.departments,
      needsErpIntegration: values.needsErpIntegration,
      participantIds: values.participants.map((p) => p.id),
    };

    if (isAdmin) {
      if (values.responsibleDepartment) payload.responsibleDepartment = values.responsibleDepartment;
      if (values.estimateDays !== undefined) payload.estimateDays = values.estimateDays;
      if (values.internalNotes.trim()) payload.internalNotes = values.internalNotes.trim();
      payload.status = values.status;
      if (request.status === "draft" && values.supervisor) payload.supervisorId = values.supervisor.id;
    }

    updateRequest.mutate(payload, { onSuccess: () => navigate(`/requests/${id}`) });
  });

  return (
    <Page width="wide">
      <Card>
        <BackButton
          label={t("requests.backToRequest")}
          onClick={() => navigate(`/requests/${id}`)}
          className={styles.backButton}
        />
        <Form noValidate onSubmit={onSubmit}>
          <Field.Input
            label={t("requests.title")}
            disabled={locked}
            error={errors.title?.message}
            {...register("title", {
              validate: (value) => hasTrimmedValue(value) || t("common.required"),
            })}
          />
          <Field.Textarea
            label={t("requests.description")}
            info={t("common.descriptionHint")}
            infoTitle={t("requests.description")}
            disabled={locked}
            error={errors.description?.message}
            {...register("description", {
              validate: (value) => hasTrimmedValue(value) || t("common.required"),
            })}
          />
          <Form.Field
            label={t("requests.audience")}
            infoTitle={t("requests.audience")}
            info={
              <>
                <p><strong>{t("requests.audienceInternal")}</strong> — {t("requests.audienceInternalInfo")}</p>
                <p><strong>{t("requests.audienceExternal")}</strong> — {t("requests.audienceExternalInfo")}</p>
              </>
            }
          >
            <Controller
              name="audience"
              control={control}
              render={({ field }) => (
                <FilterBar
                  value={field.value}
                  options={[
                    { value: "internal", label: t("requests.audienceInternal") },
                    { value: "external", label: t("requests.audienceExternal") },
                  ]}
                  onChange={field.onChange}
                  disabled={locked}
                />
              )}
            />
          </Form.Field>
          <Controller
            name="departments"
            control={control}
            render={({ field }) => (
              <DepartmentSelect
                label={t("requests.departments")}
                selected={field.value}
                onChange={field.onChange}
                disabled={locked}
              />
            )}
          />
          <Switch
            label={t("requests.needsErpIntegration")}
            info={t("requests.needsErpIntegrationInfo")}
            infoTitle={t("requests.needsErpIntegration")}
            disabled={locked}
            {...register("needsErpIntegration")}
          />
          <Form.Field
            label={t("requests.participants")}
            infoTitle={t("requests.participants")}
            info={t("requests.participantsInfo")}
          >
            <Controller
              name="participants"
              control={control}
              render={({ field }) => (
                <UserSelect
                  selected={field.value}
                  onChange={field.onChange}
                  excludeIds={[request.createdBy.id]}
                />
              )}
            />
          </Form.Field>
          {isAdmin && request.status === "draft" ? (
            <Form.Field label={t("requests.supervisor")}>
              <Controller
                name="supervisor"
                control={control}
                render={({ field }) => (
                  <UserSingleSelect selected={field.value} onChange={field.onChange} />
                )}
              />
            </Form.Field>
          ) : (
            <Field.Input
              label={t("requests.supervisor")}
              disabled
              value={request.supervisor ? `${request.supervisor.firstName} ${request.supervisor.lastName}` : ""}
              onChange={() => {}}
            />
          )}
          {isAdmin && (
            <Section title={t("requests.managementSection")}>
              <Field.Select
                label={t("requests.responsibleDepartment")}
                options={responsibleDepartmentOptions}
                {...register("responsibleDepartment")}
              />
              <Field.Input
                label={t("requests.estimateDays")}
                type="number"
                error={errors.estimateDays?.message}
                {...register("estimateDays", {
                  setValueAs: toOptionalNumber,
                  validate: (value) => isOptionalNonNegativeInteger(value) || t("common.nonNegativeNumber"),
                })}
              />
              <Field.Textarea label={t("requests.internalNotes")} {...register("internalNotes")} />
              <Form.Field label={t("requests.status")}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <FilterBar value={field.value} options={statusOptions} onChange={field.onChange} />
                  )}
                />
              </Form.Field>
            </Section>
          )}
          {updateRequest.isError && <Form.Feedback type="error">{t("common.error")}</Form.Feedback>}
          <Form.Actions>
            <Button type="button" variant="secondary" onClick={() => navigate(`/requests/${id}`)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={updateRequest.isPending}>
              {t("common.save")}
            </Button>
          </Form.Actions>
        </Form>
      </Card>
    </Page>
  );
}
